import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Alert } from '@shared/ui/Alert';
import { Comment } from '@shared/ui/Comment';
import { EmptyState } from '@shared/ui/EmptyState';
import { Panel } from '@shared/ui/Panel';

import { CaseSection } from '../_shared/components';
import { acceptsReply, sharedCopy } from '../_shared/copy';
import type { CaseDetail } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { replyFormSchema, type ReplyFormValues } from './forms.schema';
import { useReplyToCase } from './mutations';

/**
 * The thread, and the reply that belongs to it.
 *
 * **One component whatever the case type.** «One queue, one thread model, one
 * resolution state» — so a report thread and a support ticket render the same
 * anatomy, and there is no message class invented for either. The party rides on
 * `data-from` and the author's name is written in every head, so colour never
 * carries the side alone.
 *
 * **The composer is drawn conditionally on the state, and never disabled.** The
 * catalog settles what the pack left open: a resolved case accepts a reply and
 * returns to open when its owner writes back, while a closed case is terminal
 * and a follow-up is a new case quoting the old identifier. A disabled textarea
 * would promise a permission that is not coming; an absent one plus the notice
 * that names the way back is the honest shape.
 *
 * **This is not the comment composer.** That hook builds a node held for review,
 * because comment moderation runs before publication; a case reply passes no
 * review and reaches its reader directly.
 */
export function CaseThread({
  caseDetail,
  onReplied,
}: {
  caseDetail: CaseDetail;
  /** Announced in the page's one polite live region. */
  onReplied: (message: string) => void;
}) {
  const fieldId = useId();
  const hintId = useId();
  const errorId = useId();

  const reply = useReplyToCase(caseDetail.id);
  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState } = useForm<ReplyFormValues>({
    resolver: zodResolver(replyFormSchema),
    defaultValues: { body: '' },
  });

  const messages = caseDetail.messages ?? [];
  const open = acceptsReply(caseDetail.caseState);
  const lastFromOperator = messages.at(-1)?.authorSide === 'OPERATOR';
  const ownerName = caseDetail.raiser?.displayName ?? sharedCopy.ownerUnknown;

  function submit(values: ReplyFormValues) {
    setFormError(null);

    reply.mutate(values.body, {
      onSuccess: () => {
        reset({ body: '' });
        onReplied(copy.reply.sent);
      },
      onError: (error) => {
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        // The refusal stays on the form: the operator's words are still on
        // screen and still theirs to send.
        setFormError(resolveErrorCopy(code, status, errors).message);
      },
    });
  }

  const fieldError = formState.errors.body?.message;
  const describedBy = [hintId, fieldError ? errorId : null].filter(Boolean).join(' ');

  return (
    <CaseSection id="case-thread" heading={copy.thread.heading}>
      {messages.length === 0 ? (
        <EmptyState message={copy.thread.empty} />
      ) : (
        <div className="rounded-md border border-border bg-surface px-4 [&>article+article]:border-t [&>article+article]:border-border">
          {messages.map((message) => (
            <Comment
              key={message.id}
              author={
                message.authorDisplayName ?? sharedCopy.authorSide[message.authorSide] ?? message.authorSide
              }
              timestamp={message.createdAt}
              body={message.body}
              fromOperator={message.authorSide === 'OPERATOR'}
            />
          ))}
        </div>
      )}

      {/*
        Informational, never guidance: the case is waiting on someone else and
        there is nothing here for the operator to do, so it takes the info kind
        and carries no `role="alert"` — an informational banner must not
        interrupt a screen reader. The same fact takes the warning kind on the
        subscriber host, where the reader *is* the one being waited on.
      */}
      {open && caseDetail.caseState === 'awaiting user' && lastFromOperator ? (
        <Alert kind="info" title={copy.thread.awaitingTitle} className="mt-4">
          {copy.thread.awaitingBody(ownerName)}
        </Alert>
      ) : null}

      {open ? (
        <div className="mt-4">
          <Panel>
            {/*
              Card padding goes from the phone measurement to the tablet one at
              `--bp-md`: 16 px leaves the field welded to the card's edge on
              anything wider than a phone.
            */}
            <div className="p-4 md:p-5">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleSubmit(submit)(event);
                }}
                className="grid gap-2"
              >
                <label htmlFor={fieldId} className="text-sm font-medium text-ink">
                  {copy.reply.label}
                </label>

                {/*
                The field and its action row stop at the reading measure past the
                tablet tier: the content column clears 1000 px on a wide screen,
                and a reply box that wide gives a line the eye cannot track back.
              */}
                <textarea
                  id={fieldId}
                  rows={4}
                  placeholder={copy.reply.placeholder}
                  aria-describedby={describedBy}
                  aria-invalid={Boolean(fieldError)}
                  className="w-full rounded-sm border border-border-strong bg-surface p-3 text-sm leading-normal text-ink [resize:vertical] md:max-w-measure"
                  {...register('body')}
                />

                <p id={hintId} className="mb-0 max-w-measure text-xs text-ink-muted">
                  {copy.reply.hint}
                </p>

                {fieldError ? (
                  <p id={errorId} className="mb-0 max-w-measure text-xs text-danger">
                    {fieldError}
                  </p>
                ) : null}

                {formError ? (
                  <p role="alert" className="mb-0 max-w-measure text-sm text-danger">
                    {formError}
                  </p>
                ) : null}

                <div className="mt-1 flex flex-wrap gap-2 md:max-w-measure">
                  {/*
                  Secondary, not primary: the top bar's create action is this
                  view's one primary button, so every committing action inside
                  the content column is secondary.
                */}
                  <button
                    type="submit"
                    disabled={reply.isPending}
                    className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {copy.reply.send}
                  </button>

                  <button
                    type="button"
                    onClick={() => reset({ body: '' })}
                    className="hit-target rounded-sm px-4 text-sm font-semibold text-ink-muted hover:bg-hover-bg hover:text-ink"
                  >
                    {copy.reply.clear}
                  </button>
                </div>
              </form>
            </div>
          </Panel>
        </div>
      ) : (
        <Alert kind="info" title={copy.thread.closedTitle} className="mt-4">
          {copy.thread.closedBody}
        </Alert>
      )}
    </CaseSection>
  );
}
